class PredictiveSearch extends HTMLElement {
	constructor() {
		super()

		this.selectors = {
			input: '[data-predictive-search-input]',
			form: 'form',
			formStatus: '[data-predictive-search-status]',
			predictiveSearchResults: '[data-predictive-search]',
			closeBtn: '.f-drawer__close'
		}

		this.domNodes = queryDomNodes(this.selectors, this)
		this.drawer = this.closest('drawer-component')
		this.input = this.domNodes.input
		this.form = this.domNodes.form
		this.formStatus = this.domNodes.formStatus
		this.predictiveSearchResults = this.domNodes.predictiveSearchResults
		this.cachedResults = {}
		this.isOpen = false
		// CVR FALLBACK: Shopify's predictive_search endpoint returns empty on this store
		// (predictive_search.performed is always false — likely Search & Discovery service
		// not enabled). Use the regular /search section endpoint as a fallback source.
		this.predictive_search_url = '/search'
		this.predictive_search_section = 'main-search'
		this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
		this.searchTerm = '';

		this.setupEventListeners()
	}

	setupEventListeners() {
		this.form.addEventListener('submit', this.onFormSubmit.bind(this))

		this.input.addEventListener('input', debounce((event) => {
			this.onChange(event)
		}, 300).bind(this))
		this.input.addEventListener('focus', this.onFocus.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.addEventListener('keyup', this.onKeyup.bind(this))
		this.addEventListener('keydown', this.onKeydown.bind(this))
		this.domNodes.closeBtn && this.domNodes.closeBtn.addEventListener('click', this.onClose.bind(this))
	}

	getQuery() {
		return this.input.value.trim()
	}

	onChange() {
		const newSearchTerm = this.getQuery();

		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#f-predictive-search-results")?.remove();
    }

		// Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(e) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) e.preventDefault()
	}

	onFocus() {    
		const currentSearchTerm = this.getQuery();

		if (!currentSearchTerm.length) return;

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
	}

	onFocusOut(e) {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		})
	}

	onClose(e) {
		this.close(true)
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true)
		event.preventDefault()

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up')
				break
			case 'ArrowDown':
				this.switchOption('down')
				break
			case 'Enter':
				this.selectOption()
				break
		}
	}

	onKeydown(e) {
		if (
			e.code === 'ArrowUp' ||
			e.code === 'ArrowDown'
		) {
			e.preventDefault()
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) return

		const moveUp = direction === 'up'
		const selectedElement = this.querySelector('[aria-selected="true"]')
		const allElements = this.querySelectorAll('li')
		let activeElement = this.querySelector('li')

		if (moveUp && !selectedElement) return

		this.formStatus.textContent = ''

		if (!moveUp && selectedElement) {
			activeElement = selectedElement.nextElementSibling || allElements[0]
		} else if (moveUp) {
			activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1]
		}

		if (activeElement === selectedElement) return

		activeElement.setAttribute('aria-selected', true)
		if (selectedElement) selectedElement.setAttribute('aria-selected', false)

		this.setLiveRegionText(activeElement.textContent)
		this.input.setAttribute('aria-activedescendant', activeElement.id)
	}

	selectOption() {
		const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')

		if (selectedProduct) selectedProduct.click()
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase()
		this.setLiveRegionLoadingState()

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey])
			return
		}

		// Cancel any in-flight request to prevent stale loading state
		if (this._abortController) {
			try { this._abortController.abort() } catch (e) {}
		}
		this._abortController = new AbortController()

		// Safety timeout: clear spinner if request hangs > 8s
		if (this._loadingTimeout) clearTimeout(this._loadingTimeout)
		this._loadingTimeout = setTimeout(() => {
			this.removeAttribute('loading')
		}, 8000)

		const url = `${this.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${this.predictive_search_section}&type=product`
		fetch(url, { signal: this._abortController.signal })
			.then((res) => {
				if (!res.ok) {
					throw new Error(res.status)
				}
				return res.text()
			})
			.then((text) => {
				const doc = new DOMParser().parseFromString(text, 'text/html')
				const section = doc.querySelector('#shopify-section-main-search') || doc.querySelector('#shopify-section-predictive-search')
				if (!section) {
					this.removeAttribute('loading')
					return
				}
				const resultsMarkup = this.buildPredictiveMarkupFromMainSearch(section, searchTerm)

				// Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );

				this.renderSearchResults(resultsMarkup)
			})
			.catch((error) => {
				// Aborted requests are expected — don't surface or close
				if (error && error.name === 'AbortError') return
				this.removeAttribute('loading')
				console.warn('[predictive-search] fetch failed:', error)
			})
			.finally(() => {
				if (this._loadingTimeout) {
					clearTimeout(this._loadingTimeout)
					this._loadingTimeout = null
				}
			})
	}

	buildPredictiveMarkupFromMainSearch(sectionEl, searchTerm) {
		const LIMIT = 6
		// Extract product cards. main-search section uses .product-grid > .f-column > .product-card
		const cardWrappers = sectionEl.querySelectorAll('.product-grid .f-column, .product-grid > li, .product-grid > div')
		const products = []
		cardWrappers.forEach((wrap) => {
			if (products.length >= LIMIT) return
			const card = wrap.querySelector('.product-card') || wrap
			if (!card) return
			const link = card.querySelector('a[href*="/products/"]')
			const url = link ? link.getAttribute('href') : '#'
			const img = card.querySelector('img')
			const imgSrc = img ? (img.getAttribute('src') || img.getAttribute('data-src') || '') : ''
			const imgAlt = img ? (img.getAttribute('alt') || '') : ''
			const titleEl = card.querySelector('.product-card__title, .product-title, h3')
			const title = titleEl ? titleEl.textContent.trim() : ''
			const priceEl = card.querySelector('.price__regular .price-item--regular, .price-item--regular, .price__sale .price-item--sale, .price-item--sale, .price-item')
			const price = priceEl ? priceEl.textContent.trim() : ''
			if (!title || !url) return
			products.push({ url, imgSrc, imgAlt, title, price })
		})

		const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))
		const itemsHtml = products.map((p, i) => `
			<li id="f-predictive-search-option-${i + 1}" role="option" aria-selected="false">
				<a href="${escapeHtml(p.url)}" class="f-predictive-search__item" tabindex="-1">
					${p.imgSrc ? `<div class="f-predictive-search__item-image f-image" style="--aspect-ratio: 1"><img src="${escapeHtml(p.imgSrc)}" alt="${escapeHtml(p.imgAlt)}" width="70" height="70" loading="lazy"></div>` : ''}
					<div class="f-predictive-search__item-content">
						<h3 class="f-predictive-search__item-heading text-product-title">${escapeHtml(p.title)}</h3>
						${p.price ? `<div class="price">${escapeHtml(p.price)}</div>` : ''}
					</div>
				</a>
			</li>
		`).join('')

		const productsBlock = products.length > 0 ? `
			<div class="f-predictive-search__result-group f-predictive-search__result-products f-predictive-search__block-products">
				<h2 id="f-predictive-search-products" class="f-predictive-search__heading font-body-bolder">商品</h2>
				<ul id="f-predictive-search-results-list" class="f-predictive-search__results-list list-none" role="listbox" aria-labelledby="f-predictive-search-products">
					${itemsHtml}
				</ul>
			</div>` : `
			<div class="f-predictive-search__result-group">
				<p class="f-predictive-search__no-results" style="padding:1rem 0;opacity:.7;font-size:.9rem;">「${escapeHtml(searchTerm)}」に一致する商品が見つかりませんでした。</p>
			</div>`

		const seeAllUrl = `/search?q=${encodeURIComponent(searchTerm)}`
		const countText = products.length > 0 ? `${products.length}件の候補` : '0件'

		return `
			<div id="f-predictive-search-results" class="f-predictive-search-results">
				${productsBlock}
			</div>
			<div id="f-predictive-search-option-search-keywords" class="f-predictive-search__list-item" role="option">
				<a href="${seeAllUrl}" class="f-predictive-search__item f-predictive-search__item--term btn btn--plain btn--with-icon text-normal font-body-bolder" tabindex="-1">
					<span data-predictive-search-search-for-text>「${escapeHtml(searchTerm)}」の検索結果をすべて見る</span>
					<svg stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 18 18" fill="none" aria-hidden="true" width="16" height="16"><path d="M2.8125 9H15.1875"/><path d="M10.125 3.9375L15.1875 9L10.125 14.0625"/></svg>
				</a>
			</div>
			<span class="f-hidden" data-predictive-search-live-region-count-value>${countText}</span>
		`
	}

	setLiveRegionLoadingState() {
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text')

		this.setLiveRegionText(this.loadingText)
		this.setAttribute('loading', true)
	}

	setLiveRegionText(statusText) {
		this.formStatus.setAttribute('aria-hidden', 'false')
		this.formStatus.textContent = statusText

		setTimeout(() => {
			this.formStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup
		this.setAttribute('results', true)

		this.setLiveRegionResults()
		this.open()
	}

	setLiveRegionResults() {
		this.removeAttribute('loading')
		const countEl = this.querySelector('[data-predictive-search-live-region-count-value]')
		if (countEl) this.setLiveRegionText(countEl.textContent)
	}

	updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

	open() {
		this.setAttribute('open', true)
		this.input.setAttribute('aria-expanded', true)
		this.isOpen = true
	}

	close(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = ''
			this.removeAttribute('results')
		}

		const selected = this.querySelector('[aria-selected="true"]')

		if (selected) selected.setAttribute('aria-selected', false)

		this.input.setAttribute('aria-activedescendant', '')
		this.removeAttribute('open')
		this.input.setAttribute('aria-expanded', false)
		this.predictiveSearchResults.removeAttribute('style')

		this.isOpen = false
	}
}

customElements.define('predictive-search', PredictiveSearch)
